// The contacts screen's own sort and shown-filter options
// (`extension-alert-client-contacts.component.ng1.ts:268-325`).
//
// ⚠️ NOT THE CANDIDATE LIST'S, AND NOT A SUBSET OF IT. Three things differ, and this screen
// was rendering the candidate list's answer to all three:
//
// - **The sort options are different orders with different labels** — "By prev. engagement"
//   and "By hiring likelihood" against the candidates' "By relevance" / "By last CV date". The
//   bar showed "By relevance", which is not an order this endpoint accepts.
// - **There is no CV-date filter at all.** The contacts filter tooltip
//   (`extension-alert-client-contacts-filter-tooltip.tpl.html`) is a FLAT list of shown
//   options — no "Group" header, no collapsible, no "Last CV date" group — so the count beside
//   "Filters" is 0 or 1 and never 2.
// - **The filter's field names are the contact ones.** `showFavoriteClientContactsOnly` and
//   its five siblings; sending the candidate spellings leaves the server's persisted filter
//   untouched, which looks like the click did nothing.
import {
  AlertClientContactSortBy,
  type AlertClientContactFilter,
  type ExtensionAlert,
} from '@woo-ext/api'

export { AlertClientContactSortBy }
export type { AlertClientContactFilter }

export interface ClientContactSortOption {
  display: string
  field: AlertClientContactSortBy
}

/** `sortOptionDefault` is the FIRST entry's display, as on candidates. */
export const CONTACT_SORT_OPTION_DEFAULT = 'By prev. engagement'

const DEFAULT_SORT: ClientContactSortOption = {
  display: CONTACT_SORT_OPTION_DEFAULT,
  field: AlertClientContactSortBy.RelevancyPastActivity,
}

export const CONTACT_SORT_OPTIONS: readonly ClientContactSortOption[] = [
  DEFAULT_SORT,
  { display: 'By hiring likelihood', field: AlertClientContactSortBy.RelevancyHiringLikelihood },
]

export interface ClientContactShowFilterOption {
  name: string
  favoritesOnly: boolean
  showHidden: boolean
  hideViewedClientContacts: boolean
  showExportedClientContactsOnly: boolean
  showMessagedClientContactsOnly: boolean
  showActionedAndUnactionedClientContacts: boolean
  cssClass: string
  cssClassForSelected?: string
  cssClassForSlimView?: string
}

export const CONTACT_SHOWN_OPTION_DEFAULT = 'All'

const noFlags = {
  favoritesOnly: false,
  showHidden: false,
  hideViewedClientContacts: false,
  showExportedClientContactsOnly: false,
  showMessagedClientContactsOnly: false,
  showActionedAndUnactionedClientContacts: false,
}

const ALL_OPTION: ClientContactShowFilterOption = {
  name: CONTACT_SHOWN_OPTION_DEFAULT,
    ...noFlags,
  cssClass: 'fal fa-fw fa-eye',
  cssClassForSelected: 'empty',
  cssClassForSlimView: 'fal fa-fw fa-eye',
}

export const CONTACT_SHOWN_OPTIONS: readonly ClientContactShowFilterOption[] = [
  ALL_OPTION,
  { name: 'Favorites', ...noFlags, favoritesOnly: true, cssClass: 'fas fa-fw fa-heart color-ext-favorite' },
  { name: 'Unviewed', ...noFlags, hideViewedClientContacts: true, cssClass: 'fas fa-fw fa-eye-slash' },
  { name: 'Archived', ...noFlags, showHidden: true, cssClass: 'fas fa-fw fa-user-slash' },
]

export const CONTACT_EXPORTED_OPTION: ClientContactShowFilterOption = {
  name: 'Exported',
  ...noFlags,
  showExportedClientContactsOnly: true,
  cssClass: 'fas fa-fw fa-upload',
}

export const CONTACT_MESSAGED_OPTION: ClientContactShowFilterOption = {
  name: 'Messaged',
  ...noFlags,
  showMessagedClientContactsOnly: true,
  cssClass: 'fas fa-fw fa-envelope',
}

/** At index 1 — "a variant of that view, not another folder", in upstream's own words. */
export const CONTACT_ACTIONED_OPTION: ClientContactShowFilterOption = {
  name: 'All + Actioned',
  ...noFlags,
  showActionedAndUnactionedClientContacts: true,
  cssClass: 'fas fa-fw fa-users',
}

export interface ContactShownOptionsFlags {
  /** `bulkProfileActions` has Export or ExportToCsv. */
  exportClientContactsEnabled?: boolean
  /** `bulkProfileActions` has Message. */
  messageClientContactsEnabled?: boolean
  actionedAndUnactionedEnabled?: boolean
}

/**
 * `setBulkProfileActions` (`:543`) — and note what is NOT in the condition: the contacts
 * screen's "Exported" folder is gated on an export action alone, with no `addToAtsJobEnabled`
 * clause, because you do not add a contact to a job.
 */
export function contactShownOptionsFor(
  flags: ContactShownOptionsFlags = {},
): readonly ClientContactShowFilterOption[] {
  const options = [...CONTACT_SHOWN_OPTIONS]
  if (flags.exportClientContactsEnabled === true) options.push(CONTACT_EXPORTED_OPTION)
  if (flags.messageClientContactsEnabled === true) options.push(CONTACT_MESSAGED_OPTION)
  if (flags.actionedAndUnactionedEnabled === true) options.splice(1, 0, CONTACT_ACTIONED_OPTION)
  return options
}

/** `getNumberOfActiveFilters` (`:2065`) — ONE filter on this screen, so 0 or 1. */
export function contactActiveFilterCount(shownName: string): number {
  return Number(shownName !== CONTACT_SHOWN_OPTION_DEFAULT)
}

/** `cssClassForSlimView || cssClassForSelected || cssClass`, with upstream's `empty` sentinel. */
export function contactSelectedOptionIcon(option: ClientContactShowFilterOption): string | undefined {
  if (option.cssClassForSlimView !== undefined) return option.cssClassForSlimView
  if (option.cssClassForSelected === 'empty') return undefined
  return option.cssClassForSelected ?? option.cssClass
}

/**
 * `changeShownClientContacts` (`:649`) — all six flags every time, for the reason the
 * candidate twin spells out: the server merges onto the filter it persisted for the alert.
 */
export function buildClientContactFilter(shown: ClientContactShowFilterOption): AlertClientContactFilter {
  return {
    showFavoriteClientContactsOnly: shown.favoritesOnly,
    showHiddenClientContacts: shown.showHidden,
    hideViewedClientContacts: shown.hideViewedClientContacts,
    showExportedClientContactsOnly: shown.showExportedClientContactsOnly,
    showMessagedClientContactsOnly: shown.showMessagedClientContactsOnly,
    showActionedAndUnactionedClientContacts: shown.showActionedAndUnactionedClientContacts,
  }
}

/**
 * `syncShownOptions` (`:587`) — the alert PERSISTS the view, so the bar has to be seeded from
 * it rather than from the default; an alert last left on "Archived" otherwise came back
 * showing archived contacts under a bar that said "All". The field names on the alert are
 * `filterShow*Contacts*`, a third spelling again.
 */
export function contactShownOptionForAlert(
  alert: ExtensionAlert | undefined,
  options: readonly ClientContactShowFilterOption[] = CONTACT_SHOWN_OPTIONS,
): ClientContactShowFilterOption {
  const fallback = options[0] ?? ALL_OPTION
  if (alert === undefined) return fallback
  const found = options.find(
    (option) =>
      option.favoritesOnly === (alert.filterShowFavoritesContactsOnly === true) &&
      option.showHidden === (alert.filterShowHiddenContacts === true) &&
      option.hideViewedClientContacts === (alert.filterHideViewedContacts === true) &&
      option.showExportedClientContactsOnly === (alert.filterShowExportedContactsOnly === true) &&
      option.showMessagedClientContactsOnly === (alert.filterShowMessagedContactsOnly === true) &&
      option.showActionedAndUnactionedClientContacts === (alert.filterShowActionedAndUnactionedContacts === true),
  )
  return found ?? fallback
}

/** `syncSortingDisplay` (`:580`) — the display string for the alert's persisted order. */
export function contactSortOptionForAlert(sortBy: string | undefined): ClientContactSortOption {
  return CONTACT_SORT_OPTIONS.find((option) => option.field === sortBy) ?? DEFAULT_SORT
}
